/**
 * Références de paiement ISO 20022 / Swiss Payment Standards.
 *
 * Miroir front de `EVA-BACK/helpers/payment_reference.py` : mêmes règles, même verdict.
 * Le back reste l'autorité (il bloque la génération du fichier) ; ce helper sert à le dire
 * à l'utilisateur au moment de la saisie plutôt qu'au moment du paiement.
 *
 * Deux formats structurés :
 * - QRR : référence QR, 27 chiffres, clé de contrôle « modulo 10 récursif » (héritée BVR/ESR).
 * - SCOR : Creditor Reference ISO 11649, « RF » + 2 chiffres de contrôle mod-97 + 1 à 21 alphanum.
 *
 * Une clé fausse fait rejeter le fichier de paiement ENTIER par la banque (UBS : CH16 / V2791),
 * pas seulement la ligne fautive.
 */

export const QRR_LENGTH = 27;
export const SCOR_MIN_LENGTH = 5;
export const SCOR_MAX_LENGTH = 25;

/** Table de report du modulo 10 récursif (norme BVR/ESR). */
const ESR_CARRY_TABLE: readonly (readonly number[])[] = [
  [0, 9, 4, 6, 8, 2, 7, 1, 3, 5],
  [9, 4, 6, 8, 2, 7, 1, 3, 5, 0],
  [4, 6, 8, 2, 7, 1, 3, 5, 0, 9],
  [6, 8, 2, 7, 1, 3, 5, 0, 9, 4],
  [8, 2, 7, 1, 3, 5, 0, 9, 4, 6],
  [2, 7, 1, 3, 5, 0, 9, 4, 6, 8],
  [7, 1, 3, 5, 0, 9, 4, 6, 8, 2],
  [1, 3, 5, 0, 9, 4, 6, 8, 2, 7],
  [3, 5, 0, 9, 4, 6, 8, 2, 7, 1],
  [5, 0, 9, 4, 6, 8, 2, 7, 1, 3],
];

const isDigits = (value: string): boolean => value.length > 0 && /^[0-9]+$/.test(value);
const isAsciiAlphanumeric = (value: string): boolean => value.length > 0 && /^[0-9A-Z]+$/.test(value);

export function normalizePaymentReference(reference: string | null | undefined): string {
  // `String()` défensif : la référence peut venir d'une réponse API non typée à l'exécution.
  return String(reference ?? "")
    .replace(/\s/g, "")
    .toUpperCase();
}

/** Clé de contrôle attendue pour les 26 premiers chiffres d'une référence QR. */
export function qrrCheckDigit(first26Digits: string): number {
  // Sans ce garde, un caractère non numérique renvoie NaN silencieusement — donc une
  // comparaison toujours fausse plutôt qu'une erreur visible.
  if (!isDigits(first26Digits)) {
    throw new Error(`qrrCheckDigit attend des chiffres, reçu « ${first26Digits} »`);
  }
  let carry = 0;
  for (const digit of first26Digits) {
    carry = ESR_CARRY_TABLE[carry][Number(digit)];
  }
  return (10 - carry) % 10;
}

export function isValidQrr(reference: string): boolean {
  if (reference.length !== QRR_LENGTH || !isDigits(reference)) {
    return false;
  }
  return qrrCheckDigit(reference.slice(0, 26)) === Number(reference[26]);
}

/**
 * Chiffres de contrôle ISO 7064 MOD 97-10 attendus pour le corps d'une référence RF.
 * Le modulo est calculé par blocs : la chaîne convertie dépasse la précision d'un number.
 */
export function scorCheckDigits(body: string): number {
  return 98 - mod97(`${body}RF00`);
}

function mod97(value: string): number {
  let remainder = 0;
  for (const char of value.toUpperCase()) {
    const converted = /[0-9]/.test(char) ? char : (char.charCodeAt(0) - 55).toString();
    remainder = Number(`${remainder}${converted}`) % 97;
  }
  return remainder;
}

export function isValidScor(reference: string): boolean {
  if (reference.length < SCOR_MIN_LENGTH || reference.length > SCOR_MAX_LENGTH) {
    return false;
  }
  if (!reference.startsWith("RF") || !isDigits(reference.slice(2, 4))) {
    return false;
  }
  const body = reference.slice(4);
  if (!isAsciiAlphanumeric(body)) {
    return false;
  }
  return mod97(`${body}${reference.slice(0, 4)}`) === 1;
}

export enum ReferenceKind {
  QRR = "QRR",
  SCOR = "SCOR",
  UNSTRUCTURED = "UNSTRUCTURED",
}

export interface PaymentReferenceVerdict {
  kind: ReferenceKind;
  /** Référence normalisée à émettre ; vide pour une remise non structurée. */
  reference: string;
  /** Message destiné à l'utilisateur si la saisie prétend être structurée sans l'être. */
  error: string | null;
}

/**
 * Normalise une référence saisie et détermine comment elle sera émise dans le pain.001.
 *
 * Miroir de `classify_payment_reference` côté back : une saisie qui ne prétend être ni une QRR
 * ni une SCOR n'est pas une erreur — elle partira en communication libre.
 */
export function classifyPaymentReference(reference: string | null | undefined): PaymentReferenceVerdict {
  const free = (error: string | null = null): PaymentReferenceVerdict => ({
    kind: ReferenceKind.UNSTRUCTURED,
    reference: "",
    error,
  });

  const cleaned = normalizePaymentReference(reference);
  if (!cleaned) {
    return free();
  }

  if (isDigits(cleaned)) {
    // Le zéro de tête d'une référence QR est souvent perdu par les scans et les saisies. On le
    // restitue, mais à une longueur près seulement : padder un nombre court reviendrait à
    // inventer une référence, et une chaîne numérique quelconque a une chance sur dix de
    // satisfaire la clé de contrôle par accident.
    const padded = cleaned.length === QRR_LENGTH - 1 ? cleaned.padStart(QRR_LENGTH, "0") : cleaned;
    if (isValidQrr(padded)) {
      return { kind: ReferenceKind.QRR, reference: padded, error: null };
    }
    if (cleaned.length < QRR_LENGTH - 1) {
      return free();
    }
    if (padded.length !== QRR_LENGTH) {
      return free(`Référence QR invalide : ${QRR_LENGTH} chiffres attendus, ${cleaned.length} reçus.`);
    }
    const expectedDigit = qrrCheckDigit(padded.slice(0, 26));
    return free(`Référence QR invalide : clé de contrôle erronée (dernier chiffre attendu : ${expectedDigit}).`);
  }

  if (cleaned.startsWith("RF")) {
    if (isValidScor(cleaned)) {
      return { kind: ReferenceKind.SCOR, reference: cleaned, error: null };
    }
    const body = cleaned.slice(4);
    if (isDigits(cleaned.slice(2, 4)) && isAsciiAlphanumeric(body)) {
      const expected = scorCheckDigits(body).toString().padStart(2, "0");
      return free(
        `Référence invalide : clé de contrôle ISO 11649 erronée (attendu « RF${expected} » ` +
          `au lieu de « ${cleaned.slice(0, 4)} »). Corrigez la référence, ou videz ce champ et ` +
          `saisissez l'information dans « Infos additionnelles ».`,
      );
    }
    return free(
      `Référence invalide : une référence RF suit la norme ISO 11649 (« RF » + 2 chiffres de ` +
        `contrôle + 1 à 21 caractères alphanumériques). Videz ce champ et saisissez ` +
        `l'information dans « Infos additionnelles ».`,
    );
  }

  return free();
}

/** QR-IBAN : IBAN CH ou LI dont l'IID (positions 5 à 9) est compris entre 30000 et 31999. */
export function isQrIban(iban: string | null | undefined): boolean {
  const cleaned = normalizePaymentReference(iban);
  const iid = cleaned.substring(4, 9);
  return ["CH", "LI"].includes(cleaned.substring(0, 2)) && /^[0-9]{5}$/.test(iid) && +iid >= 30000 && +iid <= 31999;
}

/** Miroir de `classify_payment_reference_for_iban` côté back : un QR-IBAN n'accepte qu'une QRR. */
export function isReferenceInvalidForQrIban(
  iban: string | null | undefined,
  reference: string | null | undefined,
): boolean {
  return isQrIban(iban) && classifyPaymentReference(reference).kind !== ReferenceKind.QRR;
}

/** Message d'erreur destiné à l'utilisateur, ou `null` si la référence est acceptable. */
export function getPaymentReferenceError(reference: string | null | undefined): string | null {
  return classifyPaymentReference(reference).error;
}
